import { cn } from '@cobliteam/ui/utils';
import type { Variants } from 'motion/react';
import { motion, useAnimation } from 'motion/react';
import type { HTMLAttributes } from 'react';
import { forwardRef, useEffect } from 'react';

export interface CircleHelpIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface CircleHelpIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  isHovered: boolean;
}

const variants: Variants = {
  normal: { rotate: 0 },
  animate: { rotate: [0, -10, 10, -10, 0] },
};

const CircleHelpIcon = forwardRef<CircleHelpIconHandle, CircleHelpIconProps>(({ onMouseEnter, onMouseLeave, className, size = 28, isHovered, ...props }, ref) => {
  const controls = useAnimation();

  useEffect(() => {
    const animate = async () => {
      if (isHovered) {
        await controls.start('fadeOut');
        controls.start('fadeIn');
      } else {
        controls.start('normal');
      }
    };
    animate();
  }, [isHovered, controls]);

  return (
    <div className={cn(className)} {...props}>
      <svg
        data-testid='lucide-circle-help'
        xmlns='http://www.w3.org/2000/svg'
        width={18}
        height={18}
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      >
        <circle cx='12' cy='12' r='10' />
        <motion.g
          variants={variants}
          transition={{
            duration: 0.5,
            ease: 'easeInOut',
          }}
          animate={controls}
        >
          <path d='M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3' />
          <path d='M12 17h.01' />
        </motion.g>
      </svg>
    </div>
  );
});

CircleHelpIcon.displayName = 'CircleHelpIcon';

export { CircleHelpIcon };
